import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { LatLng, MapOptions, Marker, TileLayerOptions } from "leaflet";
import { QueryFormLocationInterface, StoreViewInterface } from "../models/store";
import { Observable } from "rxjs";
import { PageService } from "@gea-waldviertler-website/shop-lib";
import { DistinctProductInterfaceType, KeyValueInterface } from "@gea-waldviertler-website/gea-models";

export interface CoordinatesChangedInterface {
  currentPosition: LatLng,
  shownPositions: LatLng[]
}
@Component({
    selector: 'gea-shop-osm-component',
    styleUrls: [],
    template: `
    <div class="map-container" #mapContainer></div>
  `,
    standalone: false
})
export class OpenstreetmapComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('mapContainer', { read: ElementRef, static: false }) mapContainer!: ElementRef<HTMLElement>;
  @Input() options?: MapOptions
  @Input() tileLayerUrlTemplate: string
  @Input() tileLayerOptions: TileLayerOptions
  @Input() locations?: StoreViewInterface[]
  @Input() customerLocation?: QueryFormLocationInterface
  @Input() distinctProduct?: DistinctProductInterfaceType
  @Input() quantity: number = 1

  @Output("coordinatesChange") changeCoordinatesEvent = new EventEmitter<CoordinatesChangedInterface>()
  @Output("markerClick") markerClickEvent = new EventEmitter<StoreViewInterface>()

  /**
   * the map instance (NewClass)
   */
  leafletMap: any
  /**
   * the leaflet namespace (L)
   */
  leaflet: any
  markers: Marker[] = []
  customerMarker: Marker | null = null

  storePickupEnabled: boolean = true

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(PageService) private pageService: PageService,
  ) {
    // Set OSM defaults if no input given
    this.tileLayerUrlTemplate = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
    this.tileLayerOptions = {
      maxZoom: 19,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }
    this.pageService.getConfigSubject().subscribe(config => {
      if (config) this.storePickupEnabled = config.storePickupEnabled
    })
  }

  ngOnDestroy(): void {
    if (this.leafletMap) this.leafletMap.off()
  }

  ngOnInit(): void {
    this.initialize()
  }

  private initialize(): void {
    if (isPlatformBrowser(this.platformId)) {

      if (this.leafletMap) {
        this.leafletMap.remove()
      }

      if (! this.leaflet) {
        import('leaflet').then((L: any) => {
          this.leaflet = L.default || L
          this.initializeMap()
        })
      } else {
        this.initializeMap()
      }
    }
  }

  initializeMap(): void {
    this.leafletMap = this.leaflet.map(this.mapContainer.nativeElement, this.options)

    // set tile layer from inputs
    this.leaflet.tileLayer(this.tileLayerUrlTemplate, this.tileLayerOptions).addTo(this.leafletMap);

    // listen to events
    this.leafletMap.addEventListener('zoomend', (e: any) => {
      this.onMoveOrZoom()
    })
    this.leafletMap.addEventListener("moveend", (e: any) => {
      this.onMoveOrZoom()
    })

    const paneZIndex = { quick: 653, medium: 652, unavail: 651 }

    // create panes
    Object.entries(paneZIndex).forEach(([prop, zindex]) => {
      this.leafletMap.createPane(prop)
      this.leafletMap.getPane(prop).style.zIndex = zindex
    })

    this.handleLocationLayer()

    // Ensure map takes full container size
    setTimeout(() => {
      this.leafletMap.invalidateSize()
    }, 1000)
  }

  onMoveOrZoom() {
    const that = this
    let markerPositions: LatLng[] = []
    this.leafletMap.eachLayer(function(layer: any) {
      if ((layer instanceof that.leaflet.Marker) && (that.leafletMap.getBounds().contains(layer.getLatLng()))) {
        markerPositions.push(layer.getLatLng())
      }
    })
    this.changeCoordinatesEvent.emit({
      currentPosition: this.leafletMap.getCenter(),
      shownPositions: markerPositions
    })
  }

  getMarker(color: string) {
    return this.leaflet.divIcon({ html:
        '<svg xmlns="http://www.w3.org/2000/svg" height="24" fill="none" viewBox="0 0 51 63">' +
        '<path fill="' + color + '" d="M25.5 62.333s25.5-17 25.5-36.834a25.5 25.5 0 0 0-51 0c0 19.834 25.5 36.834 25.5 36.834Z"/>' +
        '<circle cx="25.5" cy="25.5" r="8.5" style="fill:#fff"/>' +
        '</svg>',
      className: "svg-icon",
      iconSize: [12, 20],
      iconAnchor: [6, 20]
    })
  }

  handleLocationLayer() {
    // call this method after initialization of the map
    if (! this.leafletMap) {
      return
    }

    const iconConfig = { quick: '#008e43', medium: '#dd5c06', unavail: '#AFACA9' }

    // remove old markers if any
    for (let i = 0; i < this.markers.length; i++) {
      this.leafletMap.removeLayer(this.markers[i]);
    }

    const that = this

    let icons: KeyValueInterface = {}
    this.markers = []
    Object.entries(iconConfig).forEach(([prop, color]) => {
      icons[prop] = this.getMarker(color)
    })
    
    // add new markers
    this.locations?.forEach((location) => {
      let pane: string
      let icon

      if (this.distinctProduct && this.distinctProduct.product) {
        let dt = this.distinctProduct.product.getDeliveryTime(
          this.distinctProduct.selectedVariant,
          this.distinctProduct.distinctId,
          this.distinctProduct.qualityId
        )
        if (! dt || ! location.storePickupPossible) pane = "unavail"
        else if (dt.daysFrom < 8 || location.stock > 0) pane = 'quick'
        else pane = "medium"
      } else {
        pane = this.distinctProduct ? "unavail" : "quick"
      }

      icon = icons[pane]

      let marker = this.leaflet
        .marker([location.address.lat, location.address.lng], { icon: icon, pane: pane })
        .on('click', function(e: any) {
          that.leafletMap.panTo({ lat: e.latlng.lat, lng: e.latlng.lng })
          setTimeout(() => {
            that.markerClickEvent.emit(location)
          }, 150)
        })

      marker.addTo(this.leafletMap)
      location.marker = marker
      this.markers.push(marker)
    })
  }

  isOoStock() {
    if(! this.distinctProduct || ! this.distinctProduct.product) {
      return false
    }
    return this.distinctProduct.product.canBeOrdered(this.storePickupEnabled, this.quantity, this.distinctProduct.selectedVariant, this.distinctProduct.distinctId)
  }

  setCustomerLocation(location: QueryFormLocationInterface | undefined) {
    if (! this.leafletMap) return
    if (this.customerMarker) {
      this.leafletMap.removeLayer(this.customerMarker)
    }

    if (location) {
      const customMarker = this.leaflet.divIcon({ html:
        '<svg xmlns="http://www.w3.org/2000/svg" height="24" fill="none" viewBox="0 0 51 63">' +
          '<path fill="#0099FF" d="M25.5 62.333s25.5-17 25.5-36.834a25.5 25.5 0 0 0-51 0c0 19.834 25.5 36.834 25.5 36.834Z"/>' +
          '<circle cx="25.5" cy="25.5" r="8.5" style="fill:#fff"/>' +
        '</svg>',
        className: "svg-icon",
        iconSize: [12, 20],
        iconAnchor: [6, 20],
      })

      let marker = this.leaflet.marker([location.lat, location.lon], { icon: customMarker }).bindPopup(location.display_name)
      marker.addTo(this.leafletMap)
      this.customerMarker = marker
    }
  }

  locate(setView?: boolean): Observable<LatLng> {
    let o = new Observable<LatLng>(subscriber => {
      this.leafletMap.locate({ setView: !!setView }).on('locationfound', (e: any) => {
        subscriber.next(e.latlng)
      })
    })

    return o
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('customerLocation')) {
      this.setCustomerLocation(changes["customerLocation"].currentValue)
    }
    if (changes.hasOwnProperty('locations')) {
      if (this.mapContainer) this.handleLocationLayer()
    }
  }
}
